import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { supabase } from '../../lib/supabase';
import NoiRockLogo from '../NoiRockLogo';

/**
 * Minimalist light-theme shell for TCN agency_admin (ingestion ops).
 * Two pages only: Upload + Imports history.
 */
const TABS = [
  { to: '/tcn/admin/upload',            label: 'Upload',           icon: '⬆' },
  { to: '/tcn/admin/imports',           label: 'History',          icon: '⏱' },
  { to: '/tcn/admin/recruitment-bonus', label: 'Recruitment bonus', icon: '🎯' },
  { to: '/tcn/admin/settings',          label: 'Settings',         icon: '⚙' },
];

const TcnAdminLayout = ({ children, title, subtitle }) => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const [user, setUser] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!cancelled) setUser(user);
    })();
    return () => { cancelled = true; };
  }, []);

  const handleSignOut = async () => {
    await supabase.auth.signOut();
    navigate('/login-authentication', { replace: true });
  };

  const isActive = (path) => location.pathname === path;

  return (
    <div className="min-h-[100dvh] bg-background">
      {/* Top bar — paddingTop carries the iOS safe-area inset so the bar's
          bg-card fills behind the status bar (black-translucent PWA) and the
          content row never collides with the device clock. */}
      <header className="bg-card border-b border-border sticky top-0 z-30" style={{ paddingTop: 'env(safe-area-inset-top, 0px)' }}>
        <div className="max-w-5xl mx-auto px-5 md:px-8 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <NoiRockLogo size={28} />
            <div>
              <div className="font-heading font-bold text-foreground text-sm">NoiRock</div>
              <div className="text-[10px] uppercase tracking-widest text-primary font-semibold">
                {t('tcn.admin.console', 'TCN Admin')}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="text-xs text-muted-foreground hidden md:block">{user?.email ?? ''}</div>
            <button
              onClick={handleSignOut}
              className="text-xs text-muted-foreground hover:text-error transition-colors"
            >
              {t('tcn.admin.signOut', 'Sign out')}
            </button>
          </div>
        </div>
        {/* Tabs */}
        <nav className="max-w-5xl mx-auto px-5 md:px-8 flex gap-1 -mb-px">
          {TABS.map((tab) => (
            <Link
              key={tab.to}
              to={tab.to}
              className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                isActive(tab.to)
                  ? 'border-primary text-primary'
                  : 'border-transparent text-muted-foreground hover:text-foreground'
              }`}
            >
              <span className="me-1.5">{tab.icon}</span>
              {t(`tcn.admin.tab.${tab.label.toLowerCase()}`, tab.label)}
            </Link>
          ))}
        </nav>
      </header>

      {/* Heading */}
      {(title || subtitle) && (
        <div className="max-w-5xl mx-auto px-5 md:px-8 py-6">
          {title && <h1 className="font-heading text-2xl font-bold text-foreground">{title}</h1>}
          {subtitle && <p className="text-sm text-muted-foreground mt-1">{subtitle}</p>}
        </div>
      )}

      {/* Content */}
      <main className="max-w-5xl mx-auto px-5 md:px-8 pb-12">
        {children}
      </main>
    </div>
  );
};

export default TcnAdminLayout;
